import { useState, useEffect, useCallback } from "react";
import { FiUsers, FiFileText, FiAtSign, FiSettings } from "react-icons/fi";
import { LuLayoutDashboard } from "react-icons/lu";
import { useNavigate, useLocation } from "react-router-dom";
import { database } from "../../firebase";
import { get, ref } from "firebase/database";
import companyLogo from "../../companylogo.png";
import { ContactSupport } from "@mui/icons-material";
import { User } from "lucide-react";

const Sidebar = ({
  user,
  isSidebarOpen,
  setIsSidebarOpen,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [companyLogoUrl, setCompanyLogoUrl] = useState(companyLogo);
  const [companyName, setCompanyName] = useState("");
  
  // Mapping of sections to URL paths
  const sectionToUrl = {
    Dashboard: "dashboard",
    "My Profile": "profile",
    Users: "users",
    "E-Signature": "e-signature",
    Supports: "supports",
    Bills: "bills",
    Setting: "setting",
  };

  // Fetch company data based on the user ID
  const fetchUserData = useCallback(async (userId) => {
    try {
      const userRef = ref(database, `users/${userId}`);
      const userSnapshot = await get(userRef);
      const userData = userSnapshot.val();

      if (userData?.companyId) {
        const companyRef = ref(database, `companies/${userData.companyId}`);
        const companySnapshot = await get(companyRef);
        const companyData = companySnapshot.val();

        if (companyData) {
          setCompanyLogoUrl(companyData.logo);
          setCompanyName(companyData.companyName);
        }
      }
    } catch (error) {
      console.error("Error fetching data from Firebase:", error);
    }
  }, []);

  // Fetch user data when the user ID changes
  useEffect(() => {
    if (user?.uid) {
      fetchUserData(user.uid);
    }
  }, [user?.uid, fetchUserData]);

  // Function for handling section navigation
  const handleSectionClick = (section) => {
    const url = sectionToUrl[section];
    if (url) {
      navigate(`/dashboard/${url}`); // Navigate to the section's URL
      if (window.innerWidth < 1024) {
        setIsSidebarOpen(false); // Close sidebar for mobile devices
      }
    } else {
      console.error(`No URL mapping found for section: ${section}`);
    }
  };

  // Function to determine if the current section is active
  const getActiveSection = () => {
    const currentPath = location.pathname.split("/")[2]; // Get current path section
    return Object.keys(sectionToUrl).find(
      (section) => sectionToUrl[section] === currentPath
    );
  };

  return (
    <div className="relative">
      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 bg-white text-gray-600 w-64 px-6 transform transition-transform lg:static lg:translate-x-0 h-full ${
          isSidebarOpen ? "translate-x-0 z-50" : "-translate-x-full"
        }`}
      >
        <ul className="space-y-4 mb-10">
          <div className="lg:hidden px-4 py-4 border-b-2">
            <img
              src={companyLogoUrl} // Base64 or URL logo
              alt={`Company logo for ${companyName}`}
              className="h-10 sm:h-12 max-h-12"
            />
          </div>
          {/* Sidebar Links */}
          <SidebarLink
            section="Dashboard"
            activeSection={getActiveSection()}
            onClick={handleSectionClick}
            icon={<LuLayoutDashboard />}
          />
          <SidebarLink
            section="My Profile"
            activeSection={getActiveSection()}
            onClick={handleSectionClick}
            icon={<User />}
          />
          <SidebarLink
            section="Users"
            activeSection={getActiveSection()}
            onClick={handleSectionClick}
            icon={<FiUsers />}
          />
          <SidebarLink
            section="E-Signature"
            activeSection={getActiveSection()}
            onClick={handleSectionClick}
            icon={<FiAtSign />}
          />
          <SidebarLink
            section="Supports"
            activeSection={getActiveSection()}
            onClick={handleSectionClick}
            icon={<ContactSupport />}
          />
          <SidebarLink
            section="Bills"
            activeSection={getActiveSection()}
            onClick={handleSectionClick}
            icon={<FiFileText />}
          />
          <SidebarLink
            section="Setting"
            activeSection={getActiveSection()}
            onClick={handleSectionClick}
            icon={<FiSettings />}
          />
        </ul>
      </div>

      {/* Overlay for mobile when sidebar is open */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)} // Close sidebar on overlay click
        ></div>
      )}
    </div>
  );
};

const SidebarLink = ({ section, activeSection, onClick, icon }) => {
  const isActive = activeSection === section; // Check if the section is active

  return (
    <li
      className={`cursor-pointer p-2 rounded-lg hover:text-orange-600 ${
        isActive ? "text-orange-600" : ""
      }`}
      onClick={() => onClick(section)} // When clicked, it updates the active section
    >
      <div className="flex items-center justify-start gap-2">
        <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
          {icon}
        </span>
        <span className="flex-grow">{section}</span>
      </div>
    </li>
  );
};

export default Sidebar;
